(() => {
  'use strict';
  if (window.padCommerceLoaded) return;
  window.padCommerceLoaded = true;
  const strings = (() => {
    try { return JSON.parse(document.getElementById('pad-commerce-strings')?.textContent || '{}'); }
    catch { return {}; }
  })();
  const message = (key) => strings[key] || 'Please check your order and try again.';
  const root = window.Shopify?.routes?.root || '/';
  let pending = false;
  const menuControllers = new Map();
  const productData = new WeakMap();
  const closingDrawers = new WeakSet();
  const drawerCancelHandlers = new WeakSet();
  const closeDrawer = dialog => {
    if (!dialog.open || closingDrawers.has(dialog)) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {dialog.close();return;}
    closingDrawers.add(dialog);
    const from = getComputedStyle(dialog).transform;
    dialog.getAnimations().forEach(animation => animation.cancel());
    const to = matchMedia('(max-width:640px)').matches ? 'translateY(100%)' : 'translateX(100%)';
    const animation = dialog.animate([{transform:from === 'none' ? 'translate(0,0)' : from},{transform:to}], {duration:480,easing:'cubic-bezier(.4,0,.2,1)',fill:'forwards'});
    animation.finished.catch(()=>{}).finally(()=>{dialog.close();animation.cancel();closingDrawers.delete(dialog);});
  };
  const showDrawer = dialog => {
    if (dialog.open) return;
    const overflow = document.documentElement.style.overflow;
    if (!drawerCancelHandlers.has(dialog)) {dialog.addEventListener('cancel',event=>{event.preventDefault();closeDrawer(dialog);});drawerCancelHandlers.add(dialog);}
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    dialog.addEventListener('close', () => {document.documentElement.style.overflow = overflow;}, {once:true});
  };
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style:'currency',currency:window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
  const animatePrice = node => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    node.animate([{transform:'translateY(3px)'},{transform:'translateY(0)'}], {duration:220,easing:'cubic-bezier(.2,.8,.2,1)'});
  };
  const errorIn = (form, text) => {
    const region = form.querySelector('[data-pad-commerce-error]');
    if (!region) return;
    region.textContent = text;
    region.hidden = !text;
  };
  const quantityValid = (form, min) => [...form.querySelectorAll('input[type="number"]')].every(input => {
    const quantity = Number(input.value);
    return input.value.trim() !== '' && Number.isInteger(quantity) && quantity >= min && quantity <= 99;
  });
  const setBusy = (form, busy, labelKey) => {
    form.setAttribute('aria-busy', String(busy));
    form.querySelectorAll('button[type="submit"]').forEach(button => {
      if (busy) {
        button.dataset.padWasDisabled = String(button.disabled);
        button.dataset.padPreviousLabel = button.textContent;
        button.disabled = true;
        if (button.matches('[data-pad-add], [data-pad-update]')) button.textContent = message(labelKey);
      } else {
        button.disabled = button.dataset.padWasDisabled === 'true';
        button.textContent = button.dataset.padPreviousLabel;
      }
    });
    form.querySelectorAll('[data-pad-remove]').forEach(link => {
      if (busy) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
    if (!busy) form.dispatchEvent(new CustomEvent('pad:commerce-settled', {bubbles: true}));
  };
  const request = async (url, options) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {...options, credentials: 'same-origin', signal: controller.signal, headers: {'Accept':'application/json', ...options.headers}});
      let body;
      try { body = await response.json(); }
      catch { throw new Error(message('request_uncertain')); }
      if (!response.ok) {
        const text = typeof body.description === 'string' ? body.description : typeof body.message === 'string' ? body.message : message('request_error');
        throw new Error(text);
      }
      return body;
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error(message('request_uncertain'));
      throw error;
    } finally { window.clearTimeout(timeout); }
  };
  let cartDialog = null;
  let cartOpener = null;
  let cartFetch = null;
  const openCart = async (opener, keepOpen = false) => {
    if (!window.HTMLDialogElement) return false;
    cartFetch?.abort();
    const controller = new AbortController();
    cartFetch = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    if (opener) cartOpener = opener;
    try {
      const response = await fetch(`${root}cart`, {credentials:'same-origin',cache:'no-store',signal:controller.signal});
      if (!response.ok) throw new Error('Unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cart = doc.querySelector('[data-pad-cart]');
      if (!cart) throw new Error('Unavailable');
      cart.dataset.padInDrawer = 'true';
      if (!cartDialog) {
        cartDialog = document.createElement('dialog');
        cartDialog.className = 'pad-drink-dialog pad-cart-dialog';
        cartDialog.dataset.padCartDialog = '';
        cartDialog.addEventListener('close', () => cartOpener?.focus({preventScroll:true}));
        cartDialog.addEventListener('click', event => {
          const r=cartDialog.getBoundingClientRect();
          if(event.target===cartDialog && (event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom))closeDrawer(cartDialog);
        });
        document.body.append(cartDialog);
      }
      const top = document.createElement('div');
      top.className = 'pad-drink-top';
      const close = document.createElement('button');
      close.type = 'button';
      close.textContent = `${cart.dataset.padCloseLabel || 'Close'} ×`;
      close.addEventListener('click', () => closeDrawer(cartDialog));
      top.append(close);
      cartDialog.replaceChildren(top, cart);
      cart.querySelector('[data-pad-return-menu]')?.addEventListener('click', event => {
        const menu=document.querySelector('[data-pad-menu]');
        if(!menu)return;
        event.preventDefault();
        cartDialog.addEventListener('close',()=>{
          menu.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});
          menu.querySelector('summary')?.focus({preventScroll:true});
        },{once:true});
        closeDrawer(cartDialog);
      });
      cartDialog.setAttribute('aria-labelledby', cart.getAttribute('aria-labelledby'));
      initialize(cartDialog);
      document.dispatchEvent(new CustomEvent('pad:commerce-settled'));
      if (!cartDialog.open && !keepOpen) showDrawer(cartDialog);
      else if (cartDialog.open) close.focus({preventScroll:true});
      return true;
    } catch (error) {
      if (cartFetch === controller) window.location.assign(`${root}cart`);
      return false;
    } finally {clearTimeout(timeout);}
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
    const url = new URL(link.href);
    const cartPath = new URL(`${root}cart`, location.origin).pathname;
    if (url.origin !== location.origin || url.pathname.replace(/\/$/,'') !== cartPath.replace(/\/$/,'') || url.search || !window.HTMLDialogElement) return;
    if (document.querySelector('[data-pad-cart]:not([data-pad-in-drawer])')) return;
    event.preventDefault();
    openCart(link);
  });
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-pad-menu]').forEach(menu => {
      if (menu.dataset.padInitialized) return;
      menu.dataset.padInitialized = 'true';
      const controller = new AbortController();
      menuControllers.set(menu, controller);
      const signal = controller.signal;
      const filters = menu.querySelector('[data-pad-menu-filters]');
      const rail = menu.querySelector('[data-pad-menu-rail]');
      const dialog = menu.querySelector('[data-pad-drink-dialog]');
      let opener = null;
      let fetchController = null;
      let noticeTimer = 0;
      if (filters) {
        filters.hidden = false;
        filters.querySelectorAll('[data-pad-category]').forEach(button => {
          button.hidden = !!button.dataset.padCategory && ![...menu.querySelectorAll('[data-pad-product-category]')].some(card => card.dataset.padProductCategory === button.dataset.padCategory);
        });
        filters.addEventListener('click', event => {
          const button = event.target.closest('[data-pad-category]');
          if (!button) return;
          filters.querySelectorAll('button').forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
          menu.querySelectorAll('[data-pad-product-category]').forEach(card => { card.hidden = !!button.dataset.padCategory && card.dataset.padProductCategory !== button.dataset.padCategory; });
          rail.scrollLeft = 0;
        }, {signal});
      }
      const controls = menu.querySelector('[data-pad-rail-controls]');
      if (controls && rail) {
        controls.hidden = false;
        controls.addEventListener('click', event => {
          const button = event.target.closest('[data-pad-rail-step]');
          if (button) rail.scrollBy({left: Number(button.dataset.padRailStep) * rail.clientWidth * .85, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
        }, {signal});
      }
      if (!dialog || !dialog.showModal) return;
      menu.addEventListener('click', async event => {
        const link = event.target.closest('[data-pad-open-product]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
        event.preventDefault();
        opener = link;
        fetchController?.abort();
        fetchController = new AbortController();
        const currentFetch = fetchController;
        const timeout = setTimeout(() => currentFetch.abort(), 15000);
        link.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(link.href, {credentials:'same-origin',signal:currentFetch.signal});
          if (!response.ok) throw new Error('Unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const product = doc.querySelector('[data-pad-product]');
          if (!product || !menu.isConnected) throw new Error('Unavailable');
          product.dataset.padInDrawer = 'true';
          delete product.dataset.padInitialized;
          const data = JSON.parse(link.closest('article').querySelector('[data-pad-product-data]').textContent);
          productData.set(product, data);
          dialog.querySelector('[data-pad-drink-content]').replaceChildren(product);
          initialize(dialog);
          showDrawer(dialog);
          document.dispatchEvent(new CustomEvent('pad:variant-change'));
        } catch (error) {
          if (menu.isConnected && currentFetch === fetchController) window.location.assign(link.href);
        } finally {clearTimeout(timeout);link.removeAttribute('aria-busy');}
      }, {signal});
      dialog.querySelector('[data-pad-close-drink]').addEventListener('click', () => closeDrawer(dialog), {signal});
      dialog.addEventListener('click', event => {if (event.target === dialog) {const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)closeDrawer(dialog);}}, {signal});
      dialog.addEventListener('close', () => opener?.focus({preventScroll:true}), {signal});
      menu.addEventListener('pad:added', () => {
        closeDrawer(dialog);
        const notice = menu.querySelector('[data-pad-added-notice]');
        notice.hidden = false;
        clearTimeout(noticeTimer);
        noticeTimer = setTimeout(() => {notice.hidden = true;}, 7000);
      }, {signal});
      signal.addEventListener('abort', () => {fetchController?.abort();clearTimeout(noticeTimer);if(dialog.open)dialog.close();}, {once:true});
    });
    scope.querySelectorAll('[data-pad-product]').forEach(product => {
      if (product.dataset.padInitialized) return;
      product.dataset.padInitialized = 'true';
      const form = product.querySelector('form');
      if (!form) return;
      const variantSelect = form.querySelector('[data-pad-variant]');
      const quantity = form.querySelector('[name="quantity"]');
      const data = productData.get(product) || (() => {try {return {variants: JSON.parse(product.querySelector('[data-pad-variants]')?.textContent || '[]')};} catch {return null;}})();
      const addon = form.querySelector('[data-pad-addon]');
      form.querySelector('[data-pad-extras]')?.removeAttribute('hidden');
      if (quantity && product.dataset.padInDrawer) {
        const stepper = document.createElement('div');
        stepper.className = 'pad-quantity-stepper';
        quantity.before(stepper);
        const label = form.querySelector(`label[for="${quantity.id}"]`)?.textContent || '';
        for (const delta of [-1, 1]) {
          const control = document.createElement('button');
          control.type = 'button';
          control.textContent = delta === -1 ? '−' : '+';
          control.setAttribute('aria-label', `${label} ${control.textContent}`);
          control.addEventListener('click', () => {
            quantity.value = String(Math.min(99, Math.max(1, (Number(quantity.value) || 1) + delta)));
            quantity.dispatchEvent(new Event('input', {bubbles:true}));
          });
          stepper.append(control);
          if (delta === -1) stepper.append(quantity);
        }
      }
      const updatePrice = () => {
        const option = variantSelect?.selectedOptions[0];
        const button = form.querySelector('[data-pad-add]');
        const variant = data?.variants?.find(item => String(item.id) === String(variantSelect?.value || form.querySelector('[name="id"]')?.value));
        const unavailable = option ? option.dataset.available !== 'true' : button.dataset.padVariantUnavailable === 'true';
        const price = product.querySelector('[data-pad-price]');
        const count = Math.min(99,Math.max(1,Number(quantity?.value)||1));
        const extrasPrice = addon?.checked ? Number(addon.dataset.price) : 0;
        if (variant) price.textContent = money((variant.price + extrasPrice) * count);
        else if (option) price.textContent = option.dataset.price;
        button.dataset.padVariantUnavailable = String(unavailable);
        button.textContent = unavailable ? message('sold_out') : `${message('add_to_order')}${variant ? ` · ${money((variant.price + extrasPrice) * count)}` : ''}`;
        if (unavailable) button.disabled = true;
        if (!product.dataset.padInDrawer && variantSelect) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantSelect.value);
          window.history.replaceState({}, '', url);
        }
        animatePrice(price);
        form.dispatchEvent(new CustomEvent('pad:variant-change', {bubbles: true}));
      };
      variantSelect?.addEventListener('change', updatePrice);
      quantity?.addEventListener('input', updatePrice);
      addon?.addEventListener('change', updatePrice);
      if (data) updatePrice();
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = form.querySelector('[data-pad-add]');
        if (pending || button.disabled) return;
        errorIn(form, '');
        if (!quantityValid(form, 1)) { errorIn(form, message('quantity_error')); return; }
        if (button.dataset.padVariantUnavailable === 'true') { errorIn(form, message('sold_out_error')); return; }
        const body = new FormData(form);
        pending = true;
        setBusy(form, true, 'adding');
        try {
          if (addon?.checked) {
            const group = crypto.randomUUID();
            const count = Number(quantity.value);
            const properties = {};
            body.forEach((value, key) => { const match = key.match(/^properties\[(.+)\]$/); if (match) properties[match[1]] = value; });
            const items = [
              {id: body.get('id'), quantity: count, properties: {...properties, '_pad_group': group, '_pad_role': 'drink', 'Add-on': addon.dataset.title}},
              {id: addon.dataset.variantId, quantity: count, properties: {'_pad_group': group, '_pad_role': 'extra', 'For': product.querySelector('h1')?.textContent.trim() || 'Coffee'}}
            ];
            await request(`${root}cart/add.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items})});
          } else await request(`${root}cart/add.js`, {method:'POST', body});
          if (product.dataset.padInDrawer) {
            pending = false;
            setBusy(form, false);
            product.dispatchEvent(new CustomEvent('pad:added', {bubbles:true}));
            document.dispatchEvent(new CustomEvent('pad:cart-changed'));
          } else window.location.assign(product.dataset.padCartUrl || `${root}cart`);
        } catch (error) {
          errorIn(form, error.message);
          pending = false;
          setBusy(form, false);
        }
      });
    });
    scope.querySelectorAll('[data-pad-cart-form]').forEach(form => {
      if (form.dataset.padInitialized) return;
      form.dataset.padInitialized = 'true';
      const syncExtras = () => {
        const rows = [...form.querySelectorAll('[data-pad-group]')];
        rows.filter(row => row.dataset.padRole === 'drink').forEach(parent => {
          const input = parent.querySelector('[data-pad-line-key]');
          input.readOnly = false;
          parent.querySelector('[data-pad-remove]')?.removeAttribute('hidden');
          rows.filter(row => row.dataset.padRole === 'extra' && row.dataset.padGroup === parent.dataset.padGroup).forEach(child => {
            child.querySelector('[data-pad-line-key]').value = input.value;
            const count = child.querySelector('[data-pad-extra-count]');
            if (count) count.textContent = input.value;
          });
        });
      };
      syncExtras();
      form.addEventListener('input', syncExtras);
      form.addEventListener('click', event => {
        const remove = event.target.closest('[data-pad-remove]');
        if (remove && !pending) {
          event.preventDefault();
          const input = remove.closest('.pad-cart-item')?.querySelector('[data-pad-line-key]');
          if (input) {input.value = '0';syncExtras();form.requestSubmit(form.querySelector('[data-pad-update]'));}
          return;
        }
        if (pending && event.target.closest('[data-pad-remove]')) {
          event.preventDefault();
          event.stopPropagation();
        }
      });
      form.addEventListener('submit', async event => {
        syncExtras();
        const checkout = event.submitter?.name === 'checkout';
        if (pending || !quantityValid(form, 0) || (checkout && event.submitter.disabled)) {
          event.preventDefault();
          if (!quantityValid(form, 0)) errorIn(form, message('cart_quantity_error'));
          return;
        }
        if (checkout) return;
        event.preventDefault();
        errorIn(form, '');
        const updates = {};
        form.querySelectorAll('[data-pad-line-key]').forEach(input => { updates[input.dataset.padLineKey] = Number(input.value); });
        const attributes = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^attributes\[(.+)\]$/);
          if (match) attributes[match[1]] = value;
        });
        pending = true;
        setBusy(form, true, 'updating');
        try {
          await request(`${root}cart/update.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({updates, attributes})});
          if (form.closest('[data-pad-in-drawer]')) {
            pending = false;
            setBusy(form, false);
            await openCart(null, true);
            document.dispatchEvent(new CustomEvent('pad:cart-changed'));
          } else window.location.reload();
        } catch (error) {
          errorIn(form, error.message);
          pending = false;
          setBusy(form, false);
        }
      });
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [menu, controller] of menuControllers) {
      if (event.target.contains(menu)) {controller.abort();menuControllers.delete(menu);}
    }
  });
})();
